<template>
  <div class="behavior-depth">
    <el-row :gutter="20" class="card-row">
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-header">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              @change="loadAll"
            />
            <el-select v-model="websiteId" placeholder="全部网站" clearable size="small" style="width:180px; margin-left:12px" @change="loadAll">
              <el-option label="全部网站" :value="0" />
              <el-option v-for="w in websites" :key="w.id" :label="w.name" :value="w.id" />
            </el-select>
            <el-button type="primary" size="small" @click="loadAll" style="margin-left:12px">刷新</el-button>
          </div>
          <div ref="hourlyChart" class="chart-body"></div>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="chart-card">
          <div class="chart-header"><span class="chart-title">星期访问分布</span></div>
          <div ref="weeklyChart" class="chart-body"></div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="card-row">
      <el-col :span="8">
        <div class="chart-card">
          <div class="chart-header"><span class="chart-title">来源渠道分析</span></div>
          <div ref="channelChart" class="chart-body"></div>
        </div>
      </el-col>
      <el-col :span="16">
        <div class="chart-card">
          <div class="chart-header"><span class="chart-title">页面停留时长排行</span></div>
          <el-table :data="stayDurationList" stripe style="width:100%" size="small" max-height="340">
            <el-table-column type="index" label="#" width="50" />
            <el-table-column label="页面" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span class="page-url">{{ row.page_path }}</span>
              </template>
            </el-table-column>
            <el-table-column label="平均停留" width="100" align="right" sortable>
              <template slot-scope="{ row }">{{ formatDuration(row.avg_stay_seconds) }}</template>
            </el-table-column>
            <el-table-column prop="visit_count" label="访问次数" width="90" align="right" sortable />
          </el-table>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="card-row">
      <el-col :span="24">
        <div class="chart-card">
          <div class="chart-header">
            <span class="chart-title">访问路径分析（页面流转）</span>
            <el-select v-model="pathLimit" size="small" style="width:100px" @change="loadVisitPath">
              <el-option label="Top 10" :value="10" />
              <el-option label="Top 15" :value="15" />
              <el-option label="Top 20" :value="20" />
            </el-select>
          </div>
          <div ref="sankeyChart" class="chart-body" style="height:480px"></div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { statsApi, websiteApi } from '../api';
import dayjs from 'dayjs';

export default {
  name: 'BehaviorDepth',
  data() {
    return {
      dateRange: [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      websiteId: 0,
      websites: [],
      pathLimit: 15,
      stayDurationList: [],
      charts: {},
      pickerOptions: {
        shortcuts: [
          { text: '今天', onClick(picker) { const d = dayjs().format('YYYY-MM-DD'); picker.$emit('pick', [d, d]); } },
          { text: '近7天', onClick(picker) { picker.$emit('pick', [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
          { text: '近30天', onClick(picker) { picker.$emit('pick', [dayjs().subtract(29, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
          { text: '本月', onClick(picker) { picker.$emit('pick', [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
        ]
      }
    };
  },
  mounted() {
    this.loadWebsites();
    this.loadAll();
    window.addEventListener('resize', this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts);
    Object.values(this.charts).forEach(c => c && c.dispose());
  },
  methods: {
    async loadWebsites() {
      try {
        const res = await websiteApi.list({ page: 1, page_size: 100 });
        if (res.code === 0) this.websites = res.data.list;
      } catch (e) { console.error(e); }
    },
    async loadAll() {
      const [startDate, endDate] = this.dateRange || [];
      const params = { start_date: startDate, end_date: endDate, website_id: this.websiteId };
      this.loadHourlyChart(params);
      this.loadWeeklyChart(params);
      this.loadChannelChart(params);
      this.loadStayDuration(params);
      this.loadVisitPath();
    },
    async loadHourlyChart(params) {
      try {
        const res = await statsApi.hourlyDistribution(params);
        if (res.code !== 0) return;
        const d = res.data;
        this.$nextTick(() => {
          const el = this.$refs.hourlyChart;
          if (!el) return;
          if (this.charts.hourly) this.charts.hourly.dispose();
          this.charts.hourly = echarts.init(el);
          this.charts.hourly.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            legend: { data: ['访问量', '独立访客'], top: 5, textStyle: { fontSize: 12 } },
            grid: { left: 50, right: 20, top: 45, bottom: 30 },
            xAxis: { type: 'category', data: d.hours, axisLabel: { fontSize: 11, color: '#64748b', interval: 1 }, axisLine: { lineStyle: { color: '#cbd5e1' } } },
            yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f3f5' } } },
            series: [
              { name: '访问量', type: 'bar', data: d.visits, barMaxWidth: 22, itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'#5b8def'},{offset:1,color:'rgba(91,141,239,0.15)'}]), borderRadius: [3,3,0,0] } },
              { name: '独立访客', type: 'line', smooth: true, data: d.visitors, itemStyle: { color: '#6b9e72' }, lineStyle: { width: 2 } },
            ]
          });
        });
      } catch (e) { console.error(e); }
    },
    async loadWeeklyChart(params) {
      try {
        const res = await statsApi.weeklyDistribution(params);
        if (res.code !== 0) return;
        const d = res.data;
        this.$nextTick(() => {
          const el = this.$refs.weeklyChart;
          if (!el) return;
          if (this.charts.weekly) this.charts.weekly.dispose();
          this.charts.weekly = echarts.init(el);
          this.charts.weekly.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            legend: { data: ['访问量', '独立访客'], top: 5, textStyle: { fontSize: 12 } },
            grid: { left: 50, right: 20, top: 45, bottom: 30 },
            xAxis: { type: 'category', data: d.days, axisLabel: { fontSize: 12, color: '#64748b' }, axisLine: { lineStyle: { color: '#cbd5e1' } } },
            yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f3f5' } } },
            series: [
              { name: '访问量', type: 'bar', data: d.visits, barMaxWidth: 36, itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'#6b9e72'},{offset:1,color:'rgba(107,158,114,0.15)'}]), borderRadius: [3,3,0,0] } },
              { name: '独立访客', type: 'line', smooth: true, data: d.visitors, itemStyle: { color: '#d4905a' }, lineStyle: { width: 2 } },
            ]
          });
        });
      } catch (e) { console.error(e); }
    },
    async loadChannelChart(params) {
      try {
        const res = await statsApi.sourceChannel(params);
        if (res.code !== 0) return;
        const data = res.data;
        const colors = ['#5b8def', '#6b9e72', '#d4905a', '#c76b6b'];
        this.$nextTick(() => {
          const el = this.$refs.channelChart;
          if (!el) return;
          if (this.charts.channel) this.charts.channel.dispose();
          this.charts.channel = echarts.init(el);
          this.charts.channel.setOption({
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            legend: { bottom: 5, textStyle: { fontSize: 12, color: '#64748b' } },
            series: [{
              type: 'pie', radius: ['40%', '65%'], center: ['50%', '42%'],
              data: data.map((item, i) => ({ name: item.name, value: item.count, itemStyle: { color: colors[i] } })),
              label: { fontSize: 11, color: '#64748b', formatter: '{b}\n{d}%' },
              itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            }]
          });
        });
      } catch (e) { console.error(e); }
    },
    async loadStayDuration(params) {
      try {
        const res = await statsApi.pageStayDuration({ ...params, limit: 20 });
        if (res.code !== 0) return;
        this.stayDurationList = res.data;
      } catch (e) { console.error(e); }
    },
    async loadVisitPath() {
      const [startDate, endDate] = this.dateRange || [];
      const params = { start_date: startDate, end_date: endDate, website_id: this.websiteId, limit: this.pathLimit };
      try {
        const res = await statsApi.visitPath(params);
        if (res.code !== 0) return;
        const d = res.data;
        this.$nextTick(() => {
          const el = this.$refs.sankeyChart;
          if (!el) return;
          if (this.charts.sankey) this.charts.sankey.dispose();
          this.charts.sankey = echarts.init(el);
          const colorPalette = ['#5b8def', '#6b9e72', '#d4905a', '#c76b6b', '#8b7ec8', '#5fa8c4', '#c4a857', '#6dba8a', '#64748b', '#a0aec0', '#4f8ac9', '#8ab87a', '#d6a56a', '#d08278', '#9b8fd0'];
          this.charts.sankey.setOption({
            tooltip: { trigger: 'item', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            series: [{
              type: 'sankey',
              layout: 'none',
              emphasis: { focus: 'adjacency' },
              data: d.nodes.map((n, i) => ({ name: n.name, itemStyle: { color: colorPalette[i % colorPalette.length] } })),
              links: d.links.map(l => ({ source: d.nodes[l.source].name, target: d.nodes[l.target].name, value: l.value })),
              lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.3 },
              label: { color: '#475569', fontSize: 11 },
              left: 20,
              right: 120,
              top: 20,
              bottom: 20,
            }]
          });
        });
      } catch (e) { console.error(e); }
    },
    formatDuration(seconds) {
      const s = Number(seconds) || 0;
      if (s < 60) return s + '秒';
      const m = Math.floor(s / 60);
      const r = s % 60;
      if (m < 60) return m + '分' + (r > 0 ? r + '秒' : '');
      const h = Math.floor(m / 60);
      const rm = m % 60;
      return h + '时' + (rm > 0 ? rm + '分' : '');
    },
    resizeCharts() {
      Object.values(this.charts).forEach(c => c && c.resize());
    },
  }
};
</script>

<style scoped>
.filter-bar { display: flex; align-items: center; margin-bottom: 18px; }
.card-row { margin-bottom: 18px; }
.card-row > .el-col { display: flex; }

.chart-card { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); border: 1px solid #eef0f3; overflow: hidden; width: 100%; }
.chart-header { padding: 14px 20px; border-bottom: 1px solid #f0f1f3; display: flex; align-items: center; justify-content: space-between; }
.chart-title { font-size: 14px; font-weight: 600; color: #1e293b; }
.chart-body { height: 300px; padding: 12px; }
.page-url { font-size: 12px; color: #475569; word-break: break-all; }
</style>
